import { useState } from "react";
import { FormControlLabel, Skeleton } from "@mui/material";
import { Checkbox, Tooltip } from "@avenue-8/sidekick-ui";
import RadarIcon from "@mui/icons-material/Radar";
import PersonSearchIcon from "@mui/icons-material/PersonSearchOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { useNavigate } from "react-router-dom";
import { useGetConsumption } from "@shared/generated/radar/queries/contactsController/useGetConsumption";
import { getConsumptionQueryResponseSchema } from "@shared/generated/radar/zod/contactsController/getConsumptionSchema";
import { isFetchClientError } from "@shared/apis/fetch-clients/fetch-client-error";
import { isLikelyStillSyncing } from "@modules/radar/shared/helpers/isLikelyStillSyncing";
import { PROFILE_REVIEW_PATH } from "@shared/utils/contact-briefing-links";
import { usePendingProfileReviewCount } from "@features/Contacts/hooks/usePendingProfileReviewCount";
import { RadarConsumptionDialog } from "./RadarConsumptionDialog";
import {
  CheckboxLabel,
  CountLabel,
  CountSubLabel,
  FilterPill,
  IconWrap,
  Pill,
  Row,
  TextColumn,
} from "./RadarMonitoringSummaryBar.styles";

type RadarMonitoringSummaryBarProps = {
  radarOnly: boolean;
  onRadarOnlyChange: (value: boolean) => void;
  monitoredSinceTimestamps: (string | null)[];
};

const CONSUMPTION_POLL_INTERVAL_MS = 15_000;

export const RadarMonitoringSummaryBar = ({
  radarOnly,
  onRadarOnlyChange,
  monitoredSinceTimestamps,
}: RadarMonitoringSummaryBarProps) => {
  const hasRecentlyMonitoredContact =
    monitoredSinceTimestamps.some(isLikelyStillSyncing);
  const consumptionQuery = useGetConsumption(undefined, {
    query: {
      refetchInterval: (query) =>
        isFetchClientError(query.state.error) &&
        query.state.error.status === 403 &&
        hasRecentlyMonitoredContact
          ? CONSUMPTION_POLL_INTERVAL_MS
          : false,
    },
  });
  const parsedConsumption = getConsumptionQueryResponseSchema.safeParse(
    consumptionQuery.data,
  );
  const isForbidden =
    isFetchClientError(consumptionQuery.error) &&
    consumptionQuery.error.status === 403;
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const navigate = useNavigate();
  const pendingProfileReviewCount = usePendingProfileReviewCount();
  const hasPendingProfileReviews = (pendingProfileReviewCount ?? 0) > 0;
  const pendingProfileReviewLabel = hasPendingProfileReviews
    ? `${pendingProfileReviewCount} ${pendingProfileReviewCount === 1 ? "profile" : "profiles"}`
    : "All caught up";

  return (
    <Row>
      {consumptionQuery.isLoading && (
        <Pill>
          <IconWrap>
            <RadarIcon fontSize="small" />
          </IconWrap>
          <TextColumn>
            <Skeleton variant="text" width={82} height={14} />
            <Skeleton variant="text" width={95} height={14} />
          </TextColumn>
        </Pill>
      )}
      {parsedConsumption.success && (
        <>
          <Tooltip
            title={`You're monitoring ${parsedConsumption.data.count} of ${parsedConsumption.data.maxContactsAllowed} allowed contacts and you have ${parsedConsumption.data.remaining} remaining.`}
          >
            <Pill
              style={{ cursor: "pointer" }}
              role="button"
              tabIndex={0}
              aria-haspopup="dialog"
              aria-label="View Radar contact monitoring details"
              onClick={() => setIsDialogOpen(true)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setIsDialogOpen(true);
                }
              }}
            >
              <IconWrap>
                <RadarIcon fontSize="small" />
              </IconWrap>
              <TextColumn>
                <CountLabel variant="subtitle2">
                  {parsedConsumption.data.count}/
                  {parsedConsumption.data.maxContactsAllowed} contacts
                </CountLabel>
                <CountSubLabel variant="caption">
                  monitored by Radar
                </CountSubLabel>
              </TextColumn>
            </Pill>
          </Tooltip>
          <RadarConsumptionDialog
            open={isDialogOpen}
            onClose={() => setIsDialogOpen(false)}
            count={parsedConsumption.data.count}
            maxContactsAllowed={parsedConsumption.data.maxContactsAllowed}
            remaining={parsedConsumption.data.remaining}
          />
        </>
      )}
      {isForbidden && hasRecentlyMonitoredContact && (
        <Pill>
          <IconWrap>
            <RadarIcon fontSize="small" />
          </IconWrap>
          <TextColumn>
            <CountLabel variant="subtitle2">Syncing…</CountLabel>
            <CountSubLabel variant="caption">setting up Radar</CountSubLabel>
          </TextColumn>
        </Pill>
      )}
      {isForbidden && !hasRecentlyMonitoredContact && (
        <Pill>
          <IconWrap>
            <RadarIcon fontSize="small" />
          </IconWrap>
          <TextColumn>
            <CountLabel variant="subtitle2">Not set up yet</CountLabel>
            <CountSubLabel variant="caption">
              turn on Radar for a contact
            </CountSubLabel>
          </TextColumn>
        </Pill>
      )}
      {pendingProfileReviewCount !== undefined && (
        <Tooltip
          title={
            hasPendingProfileReviews
              ? `Radar found ${pendingProfileReviewLabel} that might belong to your monitored contacts. Review them to confirm which ones match.`
              : "No profiles waiting for review. Radar lists new possible profiles here as it finds them for your monitored contacts."
          }
        >
          <Pill
            style={{ cursor: "pointer" }}
            role="link"
            tabIndex={0}
            aria-label={
              hasPendingProfileReviews
                ? `Review ${pendingProfileReviewLabel} Radar found for your contacts`
                : "All caught up, no profiles to review. Open profile review"
            }
            onClick={() => navigate(PROFILE_REVIEW_PATH)}
            onKeyDown={(event) => {
              if (event.key === "Enter") navigate(PROFILE_REVIEW_PATH);
            }}
          >
            <IconWrap>
              {hasPendingProfileReviews ? (
                <PersonSearchIcon fontSize="small" />
              ) : (
                <TaskAltIcon fontSize="small" />
              )}
            </IconWrap>
            <TextColumn>
              <CountLabel variant="subtitle2">
                {pendingProfileReviewLabel}
              </CountLabel>
              <CountSubLabel variant="caption">
                {hasPendingProfileReviews
                  ? "to review"
                  : "no profiles to review"}
              </CountSubLabel>
            </TextColumn>
          </Pill>
        </Tooltip>
      )}
      <FilterPill>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={radarOnly}
              onChange={(event) => onRadarOnlyChange(event.target.checked)}
            />
          }
          label={<CheckboxLabel variant="body1">Radar only</CheckboxLabel>}
        />
      </FilterPill>
    </Row>
  );
};
